import { useCallback } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';

import * as releaseApi from 'api/releaseApi';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { listSelectors } from 'store/reducers/releaseReducers/releaseComponentReducer/listSlice';
import {
  registrationActions,
  registrationSelectors,
} from 'store/reducers/releaseReducers/releaseEnvironmentReducer/registrationSlice';
import { ReleaseEnvironment } from 'types/releaseEnvironment';
import { useListStore } from './useListStore';
import { EditHookReturnType } from '../types';

/**
 * 環境登録 store の実装を環境から隠すための中間層
 */
export const useRegistrationStore = (): EditHookReturnType => {
  const componentList = useAppSelector(listSelectors.releaseComponents).map(
    (value) => ({ id: value.id, name: value.componentName }),
  );
  const dispatch = useAppDispatch();
  const { fetchReleaseEnvironments } = useListStore();
  const toast = useToast();
  const form = useForm<ReleaseEnvironment>({
    mode: 'all',
    defaultValues: {
      name: '',
      components: [{ id: undefined, version: '' }],
    },
  });

  const { getValues, reset, handleSubmit, control, watch } = form;

  const { fields, append, remove } = useFieldArray({
    name: 'components',
    control,
  });
  const watchFieldArray = watch('components');
  const controlledFields = fields.map((field, index) => ({
    ...field,
    ...watchFieldArray[index],
  }));

  const onAppend = () => {
    append({ id: undefined, version: '' });
  };

  const onRemove = (index: number) => {
    remove(index);
  };

  const currentModalStep = useAppSelector(
    registrationSelectors.currentModalStep,
  );

  const isOpenModal = useAppSelector(registrationSelectors.isOpenModal);

  const onOpenModal = useCallback(() => {
    dispatch(registrationActions.modalOpened());
  }, [dispatch]);

  const onCloseModal = () => {
    dispatch(registrationActions.initialize());
    reset();
  };

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchReleaseEnvironments();
  };

  const onConfirm = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION));
  };

  const onGoBack = () => {
    dispatch(registrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: ReleaseEnvironment) => {
      dispatch(loaderActions.setLoading(true));
      const apiValues: ReleaseEnvironment = {
        name: values.name,
        releasedAt: new Date().toISOString(),
        components: values.components.map((component) => ({
          id: Number(component.id),
          version: component.version,
        })),
      };
      const result = await releaseApi.addEnvironment(apiValues);
      if (result.ok) {
        dispatch(registrationActions.updateStep(REGISTRATION_STEP.RESULT));
      } else {
        toast({
          status: 'error',
          description: `環境の登録に失敗しました: ${result.err.message}`,
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: '環境の登録に失敗しました。',
      });
    },
  );

  return {
    mode: '登録',
    isOpenModal,
    currentModalStep,
    onOpenModal,
    onCloseModal,
    onCloseModalWhenCompleted,
    onConfirm,
    onGoBack,
    onSubmit,
    onOpenModalDelete: () => null,
    form,
    values: getValues(),
    onAppend,
    onRemove,
    controlledFields,
    componentList,
  };
};
